/**
 * Shop + traveller-merchant card stock + pricing (HEX-63 / HEX-72; ADR-0015 §6).
 *
 * Two card vendors share this module:
 *   - **Shop** (`rollShopStock`) — a small uniform selection over the gameplay
 *     pool plus buyable `die` fuel. Stock is rolled ONCE per shop and then
 *     *persisted* (HEX-72): the shop depletes permanently as cards are bought,
 *     no per-visit refresh. A forced restock (or buying the last card) re-rolls.
 *   - **Traveller-merchant** (`rollMerchantStock`) — a rarer frontier vendor with
 *     a small selection skewed toward higher rarity (rare/epic surface earlier
 *     than at town shops). One-time: consumed after a single purchase (§4).
 *
 * Rolls are pure + deterministic given the injected `Rng`, so the rarity mix is
 * testable by probability band (project test floor). Persistence stores the
 * card *definition* ids (`stockIds` / `stockFromIds`); the buyer mints an owned
 * instance id when the card lands in their inventory.
 */

import { GAMEPLAY_CARDS } from './cardDrops';
import { DIE_CARDS, cardById } from './cards';
import type { Rng } from './dice';
import type { Card, Rarity } from './types';

/** Gold price by card rarity (ADR-0015 §6 first-pass). `die` fuel is `common`. */
export const CARD_PRICES: Record<Rarity, number> = {
  common: 5,
  rare: 15,
  epic: 40,
  legendary: 100,
};

/** Number of cards offered by a town shop when (re)stocked. */
export const SHOP_STOCK_SIZE = 3;

/** Number of cards a traveller-merchant carries (smaller than a shop; §4). */
export const MERCHANT_STOCK_SIZE = 2;

/** Gold to force a fresh shop restock without depleting the shelf (HEX-72). */
export const SHOP_RESTOCK_PRICE = 100;

/** The shop's buyable pool: the §8 gameplay cards plus `die` fuel (HEX-72 —
 * dice are buyable). Uniform; pricing does the gating. */
export const SHOP_POOL: readonly Card[] = [...GAMEPLAY_CARDS, ...DIE_CARDS];

/** Rarity skew for traveller-merchants (HEX-72): rare/epic surface earlier than
 * at town shops. Weights sum to 1; `die` fuel rides the `common` bucket. */
export const MERCHANT_RARITY_WEIGHTS: Record<Rarity, number> = {
  common: 0.15,
  rare: 0.4,
  epic: 0.35,
  legendary: 0.1,
};

const RARITY_ORDER: readonly Rarity[] = ['common', 'rare', 'epic', 'legendary'];

export function cardPrice(card: Card): number {
  return CARD_PRICES[card.rarity];
}

/** Serialise a stock list to the persisted card-definition ids (HEX-72). */
export function stockIds(cards: readonly Card[]): string[] {
  return cards.map((c) => c.id);
}

/** Rehydrate a persisted id list back into card definitions (HEX-72). Throws on
 * an unknown id via `cardById` — a corrupt row is a bug, not a runtime branch. */
export function stockFromIds(ids: readonly string[]): Card[] {
  return ids.map((id) => cardById(id));
}

/**
 * Deterministic per-shop seed from its hex coords (HEX-72). Keeps each shop's
 * initial roll stable across reloads while two different shops differ.
 */
export function shopStockSeed(q: number, r: number): number {
  let h = 0x811c9dc5;
  for (const x of [q, r]) {
    h ^= x | 0;
    h = Math.imul(h, 0x01000193);
  }
  return h | 0;
}

/**
 * Roll `count` distinct cards (by definition id) for a shop (re)stock. Uniform
 * over `SHOP_POOL` — the rarity mix falls out of the pool's composition and
 * pricing does the gating. Pure + deterministic given `rng`.
 */
export function rollShopStock(rng: Rng, count = SHOP_STOCK_SIZE): Card[] {
  return pickDistinct(SHOP_POOL, count, rng);
}

/**
 * Roll a traveller-merchant's selection: `count` distinct cards skewed toward
 * higher rarity (`MERCHANT_RARITY_WEIGHTS`). Each slot rolls a rarity, then a
 * uniform card of that rarity (falling back to the broad pool if a rarity is
 * exhausted by earlier picks). Pure + deterministic given `rng`.
 */
export function rollMerchantStock(rng: Rng, count = MERCHANT_STOCK_SIZE): Card[] {
  const remaining = [...SHOP_POOL];
  const out: Card[] = [];
  const n = Math.min(count, remaining.length);
  for (let i = 0; i < n; i++) {
    const rarity = pickRarity(rng);
    const ofRarity = remaining.filter((c) => c.rarity === rarity);
    const pool = ofRarity.length > 0 ? ofRarity : remaining;
    const index = Math.min(pool.length - 1, Math.floor(rng() * pool.length));
    const picked = pool[index];
    if (!picked) continue;
    out.push(picked);
    remaining.splice(remaining.indexOf(picked), 1);
  }
  return out;
}

/** Weighted rarity pick for the merchant skew. Falls back to the heaviest seen
 * entry if a float rounding gap leaves `r` past the cumulative sum. */
function pickRarity(rng: Rng): Rarity {
  const r = rng();
  let cumulative = 0;
  let last: Rarity = 'common';
  for (const rarity of RARITY_ORDER) {
    const w = MERCHANT_RARITY_WEIGHTS[rarity];
    if (w <= 0) continue;
    last = rarity;
    cumulative += w;
    if (r < cumulative) return rarity;
  }
  return last;
}

/** Pick `count` distinct cards uniformly from `pool` without replacement. */
function pickDistinct(pool: readonly Card[], count: number, rng: Rng): Card[] {
  const remaining = [...pool];
  const out: Card[] = [];
  const n = Math.min(count, remaining.length);
  for (let i = 0; i < n; i++) {
    const index = Math.min(remaining.length - 1, Math.floor(rng() * remaining.length));
    const [picked] = remaining.splice(index, 1);
    if (picked) out.push(picked);
  }
  return out;
}
